<template>
  <el-dialog
    v-model="isDialogVisible"
    title="Payment"
    width="500"
    align-center
    :close-on-click-modal="false"
    @close="handleDialogClose"
  >
    <v-card-text>
      <v-label>Choose Payment Method</v-label>
      <el-select
        class="mt-2"
        required
        hide-details="auto"
        variant="outlined"
        color="primary"
        size="large"
        v-model="selectedPaymentMethod"
        :placeholder="'Choose Payment Method'"
        outlined
      >
        <el-option
          v-for="method in paymentMethods"
          :key="method.value"
          :label="method.label"
          :value="method.value"
        />
      </el-select>

      <div class="mt-2">
        <v-label>Please enter an amount</v-label>
        <v-text-field
          v-model="inputAmount"
          placeholder="300 - 200,000"
          density="comfortable"
          required
          hide-details="auto"
          variant="outlined"
          color="primary"
          maxlength="10"
          @input="
            inputAmount = inputAmount
              .replace(/[^0-9.]/g, '')
              .replace(/(\..*)\./g, '$1')
              .slice(0, 10)
          "
          :rules="[
            (v) => !!v || 'Amount is required',
            (v) => {
              const numberValue = parseFloat(v);
              return (
                (numberValue >= 300 && numberValue <= 200000) ||
                'Amount must be between 300 and 200,000'
              );
            },
            (v) =>
              /^\d+(\.\d{1,2})?$/.test(v) ||
              'Amount must be a valid decimal with up to two decimal places',
          ]"
          @keyup.enter="submitPayment"
        />
      </div>

      <div
        class="mt-2"
        v-if="selectedPaymentMethod === 1"
      >
        <div class="mt-3">
          <el-alert type="warning" show-icon class="mb-4">
            <template #title>
              <div class="fs-8">
                <p>
                  Please use only the selected bank account for your transfer.
                </p>
                <p>โปรดใช้บัญชีธนาคารที่ท่านเลือกในการโอนเงินเท่านั้น</p>
                <p>请仅使用您所选择的银行账户进行转账。</p>
              </div>
            </template>
          </el-alert>
        </div>
        <v-label
          >Choose Your Account
          <span class="c-pointer text-primary ml-1" @click="openAddDialog">
            (Or add new one)</span
          ></v-label
        >
        <el-select
          v-model="selectedAccount"
          :placeholder="'Choose Your Account'"
          density="comfortable"
          required
          hide-details="auto"
          variant="outlined"
          color="primary"
          size="large"
        >
          <el-option
            v-for="account in userAccounts"
            :key="account.value"
            :label="account.label"
            :value="account.value"
          />
        </el-select>
      </div>

      <div class="mt-2">
        <v-label>Please enter a remark</v-label>
        <v-text-field
          v-model="inputRemark"
          placeholder="Remark"
          class="mt-2"
          density="comfortable"
          required
          hide-details="auto"
          variant="outlined"
          color="primary"
          :rules="[(v) => !!v || 'Remark is required']"
          @keyup.enter="submitPayment"
          maxlength="50"
        />
      </div>
    </v-card-text>

    <template #footer>
      <div class="dialog-footer">
        <el-button
          variant="flat"
          size="large"
          rounded="md"
          type="danger"
          @click="resetFields()"
          >Cancel</el-button
        >
        <el-button
          variant="flat"
          size="large"
          rounded="md"
          type="primary"
          @click="submitPayment()"
          >Pay
        </el-button>
      </div>
    </template>
  </el-dialog>

  <el-dialog
    v-model="dialogVisible"
    title="Add Account"
    width="500"
    align-center
    :close-on-click-modal="false"
  >
    <div class="">
      <v-label>Bank</v-label>

      <el-select
        v-model="dialogData.bank"
        placeholder="Please select a bank"
        size="large"
        class="v-input__control"
        :rules="bankRules"
        required
      >
        <el-option
          v-for="bank in banks"
          :key="bank.value"
          :label="bank.label"
          :value="bank.value"
        />
      </el-select>
    </div>

    <div class="mt-4">
      <v-label>Account Number</v-label>
      <v-text-field
        aria-label="account_number"
        v-model="dialogData.account_number"
        maxlength="12"
        class="mt-2"
        density="comfortable"
        variant="outlined"
        color="primary"
        :rules="accountNumberRules"
        required
      ></v-text-field>
    </div>

    <v-row>
      <v-col cols="6">
        <v-label>First Name</v-label>
        <v-text-field
          aria-label="first_name"
          v-model="dialogData.first_name"
          class="mt-2"
          density="comfortable"
          variant="outlined"
          color="primary"
          :rules="fnameRules"
          required
        ></v-text-field>
      </v-col>
      <v-col cols="6">
        <v-label>Last Name</v-label>
        <v-text-field
          aria-label="last_name"
          v-model="dialogData.last_name"
          class="mt-2"
          density="comfortable"
          variant="outlined"
          color="primary"
          :rules="lnameRules"
          required
        ></v-text-field>
      </v-col>
    </v-row>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="closeDialog">Cancel</el-button>
        <el-button type="primary" @click="handleSubmit"> Add </el-button>
      </div>
    </template>
  </el-dialog>

  <payment-detail v-model="isDialogPayment" :selectedOrder="selectedOrder" />
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  onMounted,
  inject,
  watch,
  defineExpose,
  defineProps,
  defineEmits,
  nextTick,
} from "vue";
import { remarkForApi } from "@/core/helpers/remark";
import ApiService from "@/core/services/Transaction.ts";
import ApiBankService from "@/core/services/Account.ts";
import { LoadingService } from "@/core/plugins/loading";
import { ElNotification } from "element-plus";
import SvgSprite from "@/components/shared/SvgSprite.vue";

import { useRoute, useRouter } from "vue-router";

import PaymentDetail from "@/views/sellstock/transaction/PayDetail";

const route = useRoute();
const router = useRouter();

const $loading = inject("$loading") as LoadingService;

const items = ref([]);
const count = ref(0);
interface BankOption {
  value: string;
  label: string;
}

const banks: BankOption[] = [
  { value: "bbl", label: "Bangkok Bank" },
  {
    value: "baac",
    label: "Bank for Agriculture and Agricultural Cooperatives",
  },
  { value: "bay", label: "Bank of Ayudhya" },
  { value: "cimb", label: "CIMB Thai Bank" },
  { value: "gov", label: "Government Savings Bank" }, // gov
  { value: "kbank", label: "Kasikorn Bank" },
  { value: "kk", label: "Kiatnakin Phatra Bank" }, // kk
  { value: "ktb", label: "KTB" },
  { value: "scb", label: "SCB" },
  { value: "ttb", label: "TMB Thanachart Bank" },
  { value: "uobt", label: "United Overseas Bank (Thai)" },
];

const bankRules = ref([(v: string) => !!v || "Bank is required"]);
const fnameRules = ref([(v: string) => !!v || "First Name is required"]);
const lnameRules = ref([(v: string) => !!v || "Last Name is required"]);
const accountNumberRules = ref([
  (v: string) => !!v || "Account Number is required",
  (v: string) =>
    /^[0-9]{1,12}$/.test(v) ||
    "Account number must be numeric and no more than 12 characters.",
]);

const dialogVisible = ref(false);
const dialogData = ref({
  bank: "",
  account_number: "",
  first_name: "",
  last_name: "",
});

const openAddDialog = () => {
  dialogData.value = {
    bank: "",
    account_number: "",
    first_name: "",
    last_name: "",
  };
  dialogVisible.value = true;
};

const closeDialog = () => {
  dialogVisible.value = false;
  dialogData.value = {
    bank: "",
    account_number: "",
    first_name: "",
    last_name: "",
  };
};

const handleSubmit = async () => {
  if (
    !dialogData.value.bank ||
    !dialogData.value.account_number ||
    !dialogData.value.first_name ||
    !dialogData.value.last_name
  ) {
    ElNotification({
      title: "error",
      message: "Please fill in all fields.",
      type: "error",
    });

    return;
  }

  $loading.startLoading();

  try {
    const fullName = `${dialogData.value.first_name} ${dialogData.value.last_name}`;

    const response = await ApiBankService.addAccount({
      bank: dialogData.value.bank,
      account_number: dialogData.value.account_number,
      account_fullname: fullName,
    });

    ElNotification({
      title: "success",
      message: response.message || "Account added successfully!",
      type: "success",
    });

    fetchUserAccounts();
    closeDialog();
  } catch (error) {
    console.error(error);
    ElNotification({
      title: "error",
      message: error.response?.data?.message || "Operation failed.",
      type: "error",
    });
  } finally {
    $loading.endLoading();
  }
};

const selectedPaymentMethod = ref(1);
const selectedAccount = ref("");
const paymentMethods = ref([{ label: "QRCode", value: 1 }]);

const userAccounts = ref("");

const inputAmount = ref("");
const inputRemark = ref("");

const amountInput = ref<HTMLInputElement | null>(null);

const isDialogPayment = ref(false);
const selectedOrder = ref(null);

const props = defineProps({
  modelValue: Boolean,
});

const emit = defineEmits();

const isDialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit("update:modelValue", value);
  },
});

const resetFields = () => {
  selectedPaymentMethod.value = "";
  selectedAccount.value = "";
  inputAmount.value = "";
  inputRemark.value = "";
  isDialogVisible.value = false;
};

const handleDialogClose = () => {
  emit("update:modelValue", false);
  // if (route.path.includes("sellstock/transaction")) {
  //   window.location.reload();
  // }
  resetFields();
};

const fetchUserAccounts = async () => {
  try {
    const resp = await ApiBankService.accountLists();

    if (resp && resp.bank_accounts && Array.isArray(resp.bank_accounts)) {
      let data = resp.bank_accounts;
      userAccounts.value = data.map((account: any) => ({
        label: `${account.account_fullname} - ${account.account_number} (${account.bank})`,
        value: account.id,
      }));
    } else {
      throw new Error("No bank accounts found or invalid response format.");
    }
  } catch (error) {
    ElNotification({
      title: "Error",
      message: error,
      type: "error",
    });
  }
};
watch(selectedPaymentMethod, (newVal) => {
  if (newVal === 1) {
    fetchUserAccounts();
  }
});

const handlePay = (item: any) => {
  isDialogVisible.value = true;
};

const submitPayment = async (order) => {
  $loading.startLoading();
  if (selectedPaymentMethod.value) {
    let payload: any = {
      method: selectedPaymentMethod.value,
    };

    if (selectedPaymentMethod.value === 1) {
      if (!selectedAccount.value) {
        ElNotification({
          title: "Error",
          message: "Please select an account.",
          type: "error",
        });
        $loading.endLoading();
        return;
      }
      payload.bank_account_id = selectedAccount.value;
    }

    if (inputAmount.value < 300 || inputAmount.value > 200000) {
      ElNotification({
        title: "Error",
        message: "Amount must be between 300 and 200,000",
        type: "error",
      });
      $loading.endLoading();
      return;
    }

    payload.amount = parseFloat(inputAmount.value);
    payload.remark = remarkForApi(inputRemark.value);

    const resp = await ApiService.pay(payload);
    if (resp.payment) {
      selectedOrder.value = resp.payment;
      await nextTick();
      isDialogPayment.value = true;
      ElNotification({
        title: "Success",
        message: resp.message,
        type: "success",
      });

      resetFields();
    } else {
      ElNotification({
        title: "Error",
        message: resp.error || resp.msg,
        type: "error",
      });
    }
  } else {
    ElNotification({
      title: "Error",
      message: "Please select a payment method.",
      type: "error",
    });
  }
  $loading.endLoading();
};

const closeUploadDialog = () => {
  isDialogPayment.value = false;
  selectedOrder.value = null;
};

watch(
  () => isDialogVisible.value,
  (newVal) => {
    if (newVal) {
      selectedPaymentMethod.value = 1;
      fetchUserAccounts();
      nextTick(() => {
        const inputEl = amountInput.value?.$el?.querySelector("input");
        if (inputEl) {
          inputEl.focus();
        }
      });
    }
  }
);
</script>
